import type { TrigKey } from './keys';

// Full translation of every TrigKey into Romanian.
// Math function glyphs (sin, cos, tan, csc, sec, cot) are unchanged — same in Romanian.
// English fallback is active for any key omitted at runtime, but all keys are covered here.
export const ro: Record<TrigKey, string> = {
  // ── App chrome ──────────────────────────────────────────────────────────────
  'app.eyebrow': 'Matematică · Trigonometrie',

  // ── Side-panel labels ────────────────────────────────────────────────────────
  'panel.experiment': 'Experiment',
  'panel.params': 'Parametri',
  'panel.scene': 'Alege un experiment',
  'view.reset': 'Resetează vederea',

  // ── Experiment picker labels ─────────────────────────────────────────────────
  'experiment.right-triangle.label': 'Triunghi dreptunghic',
  'experiment.unit-circle.label': 'Cercul trigonometric',
  'experiment.wave-studio.label': 'Studio de unde',
  'experiment.identity-playground.label': 'Identități',
  'experiment.triangle-solver.label': 'Rezolvator de triunghiuri',
  'experiment.circular-polar.label': 'Mișcare circulară & polar',

  // ── Global hint ──────────────────────────────────────────────────────────────
  'hint.line1': 'Trage punctele și mișcă glisierele.',
  'hint.line2': 'Conținutul educațional trăiește în geometrie, nu în text.',
  'hint.line3':
    'Derulează sau ciupește pentru zoom, trage pentru a deplasa, dublu-clic pentru resetare.',

  // ── Right-triangle experiment ────────────────────────────────────────────────
  'rt.angle.a': 'Unghi A',
  'rt.angle.b': 'Unghi B',
  'rt.angle.value': '∠',
  'rt.units.rad': 'Radiani',
  'rt.show.sin': 'sin',
  'rt.show.cos': 'cos',
  'rt.show.tan': 'tan',
  'rt.snap': 'Aliniere la 30°/45°/60°',

  // ── Unit-circle experiment ───────────────────────────────────────────────────
  'uc.units': 'Unități',
  'uc.show.sin': 'sin',
  'uc.show.cos': 'cos',
  'uc.show.tan': 'tan',
  'uc.show.csc': 'csc',
  'uc.show.sec': 'sec',
  'uc.show.cot': 'cot',
  'uc.show.astc': 'Arată ASTC',
  'uc.snap.common': 'Aliniere la unghiuri comune',
  'uc.angle.value': '∠',
  'uc.units.deg': 'grade',
  'uc.units.rad': 'rad',
  'uc.units.both': 'grade+rad',

  // ── Wave-studio experiment ───────────────────────────────────────────────────
  'ws.fn': 'Funcție',
  'ws.fn.sin': 'sin',
  'ws.fn.cos': 'cos',
  'ws.fn.tan': 'tan',
  'ws.fn.csc': 'csc',
  'ws.fn.sec': 'sec',
  'ws.fn.cot': 'cot',
  'ws.amplitude': 'A — Amplitudine',
  'ws.frequency': 'B — Frecvență',
  'ws.phase': 'C — Defazaj',
  'ws.shift': 'D — Deplasare verticală',
  'ws.rform.toggle': 'Derivare formă-R',
  'ws.rform.a': 'a (coef. sin)',
  'ws.rform.b': 'b (coef. cos)',
  'ws.rform.r': 'R',
  'ws.rform.alpha': 'α',
  'ws.reset': 'Resetare',
  'ws.base': 'Arată curba de bază',
  'ws.functions': 'Funcții',
  'ws.add': 'Adaugă funcție',
  'ws.remove': 'Elimină funcția',
  'ws.visible': 'Arată sau ascunde funcția',
  'ws.showParams': 'Arată parametrii',

  // ── Identity-playground experiment ──────────────────────────────────────────
  'ip.id.pythagorean': 'Pitagoreică',
  'ip.id.reciprocal': 'Reciprocă',
  'ip.id.quotient': 'Câtului',
  'ip.id.sum': 'Sumei',
  'ip.id.difference': 'Diferenței',
  'ip.id.double': 'Unghi dublu',
  'ip.id.half': 'Unghi înjumătățit',
  'ip.play': 'Redă',
  'ip.pause': 'Pauză',
  'ip.step': 'Pas',
  'ip.reset': 'Resetare',
  'ip.proof.pythagorean.summary': 'sin²θ + cos²θ = 1',
  'ip.proof.reciprocal.summary': 'csc θ = 1/sin θ,  sec θ = 1/cos θ,  cot θ = 1/tan θ',
  'ip.proof.quotient.summary': 'tan θ = sin θ / cos θ',
  'ip.proof.sum.summary': 'sin(A+B) = sin A cos B + cos A sin B',
  'ip.proof.difference.summary': 'sin(A−B) = sin A cos B − cos A sin B',
  'ip.proof.double.summary': 'sin 2θ = 2 sin θ cos θ',
  'ip.proof.half.summary': 'sin(θ/2) = ±√((1 − cos θ)/2)',
  'ip.proof.v2.note':
    'Disponibil în v2 — această demonstrație necesită o construcție mai elaborată.',

  // ── Triangle-solver experiment ───────────────────────────────────────────────
  'ts.area': 'Arie',
  'ts.sineRule': 'Regula sinusurilor',
  'ts.cosineRule': 'Regula cosinusurilor',
  'ts.ambiguous.toggle': 'Arată triunghiul fantomă la caz ambiguu',
  'ts.reset': 'Resetare la echilateral',
  'ts.snap.integers': 'Aliniere la întregi',
  'ts.side.a': 'a',
  'ts.side.b': 'b',
  'ts.side.c': 'c',
  'ts.angle.A': 'A',
  'ts.angle.B': 'B',
  'ts.angle.C': 'C',
  'ts.section.sides': 'Laturi',
  'ts.section.angles': 'Unghiuri',
  'ts.section.sineRule': 'Regula sinusurilor',
  'ts.section.cosineRule': 'Regula cosinusurilor',
  'ts.section.area': 'Arie',

  // ── Circular-polar experiment ────────────────────────────────────────────────
  'cp.omega': 'Viteză unghiulară',
  'cp.plot.y': 'Grafic y (sin)',
  'cp.plot.x': 'Grafic x (cos)',
  'cp.polar.label': 'Figură polară',
  'cp.polar.circle': 'Cerc (r = 1)',
  'cp.polar.rose': 'Rozetă (r = sin 3θ)',
  'cp.polar.cardioid': 'Cardioida (r = 1 + cos θ)',
  'cp.polar.limacon': 'Limaçon (r = 1 − 2 cos θ)',
  'cp.smallAngle': 'Aprox. unghi mic (sin x ≈ x)',
  'cp.play': 'Redă',
  'cp.pause': 'Pauză',
  'cp.reset': 'Resetare',
  'cp.step': 'Pas π/12',
};
